<script setup lang="ts">
definePageMeta({ layout: 'marketing' })
</script>

<template>
  <MarketingVerticalLandingPage
    vertical="migration-agencies"
    accent_from="from-emerald-500"
    accent_to="to-teal-500"
    :hero="{
      eyebrow: 'For MARA-registered migration agents',
      headline: 'DHA paperwork, finally not in a folder named “final_v3”.',
      subhead: 'Subclass-specific checklists, lodgement deadlines, AAT review windows, and a client portal that ends the email-attachment carousel. Built around the way Australian migration practices actually work.',
      cta_text: 'See the visa workflow',
    }"
    :features="[
      { icon: '🛂', title: 'Visa subclass library',     description: 'Built-in checklists for 189/190/491, 482/186, 820/801, 309/100, 500, 485, 600 and more. Add your own subclasses too.' },
      { icon: '📋', title: 'Evidence chasing on rails', description: 'Document slots per subclass with auto-reminders. Stop chasing AFP checks and Form 80 by hand.' },
      { icon: '⏰', title: 'Lodgement + decision clock', description: 'Skills assessment, lodgement, AAT review windows tracked as case deadlines — flagged before they bite.' },
      { icon: '🔐', title: 'Magic-link client portal',   description: 'Clients upload from a phone. No new password. No “did you get my email?” at 9pm.' },
      { icon: '💼', title: 'MARN + engagement letter',   description: 'Tenant-level MARN field, engagement letter on the case file, fees quoted in AUD, all in one place.' },
      { icon: '🇦🇺', title: 'AU-first by default',        description: 'Sydney/Melbourne/Brisbane timezones, AUD pricing, references to DHA / Home Affairs / AAT — not generic CRM jargon.' },
    ]"
    :workflow_steps="[
      { step: '1', title: 'Enquiry + consultation',  description: 'Public enquiry form auto-opens a matter. Free 30-minute consultation booked from your calendar. No double-entry.' },
      { step: '2', title: 'Engagement + evidence',   description: 'Engagement letter signed, document slots populate by subclass, automated reminders chase missing pieces.' },
      { step: '3', title: 'Lodgement + decision',    description: 'Lodged with DHA, decision-due deadline set, RFI handling and grant notice all on the same case file.' },
    ]"
    :pain_points="[
      'Subclass checklists living in your head and three Word docs',
      'AAT review windows missed because nobody owned the deadline',
      'Clients emailing passports to your personal Gmail',
      'Engagement letters and fee status in a spreadsheet on someone’s desktop',
    ]"
    :relief_points="[
      'Per-subclass checklists shipped on day one',
      'Decision-due + AAT clocks visible on every matter',
      'Clients upload from a magic-link portal — no app, no password',
      'Fees, VAC payments and engagement letter on the case file',
    ]"
    :sample_screen="{
      title: 'Patel, A. — Subclass 189',
      subtitle: 'Matter #2189',
      items: [
        { label: 'Subclass',         value: '189 Skilled Independent' },
        { label: 'DHA file ref',     value: 'EGOM-4421' },
        { label: 'Lodged',           value: '08 Apr 2026' },
        { label: 'Decision expected',value: '05 Oct 2026' },
        { label: 'Bridging visa',    value: 'BVA' },
      ],
    }"
    :faq="[
      { q: 'Does it lodge with DHA for me?',                a: 'No — lodgement happens through your ImmiAccount. We track every required document, deadline, and RFI so nothing’s missing when you do lodge.' },
      { q: 'Can my clients upload documents themselves?',   a: 'Yes — every matter has a magic-link portal. Clients open it from a phone, drop passport scans, and you see them on the file immediately.' },
      { q: 'Is my MARN tracked anywhere?',                  a: 'MARN + expiry sit on the tenant profile and surface in the footer of every client-facing document. You can update it once a year, not on every letter.' },
      { q: 'What about AAT review timelines?',              a: 'AAT review is a stage on the visa workflow with its own filing window. Miss-it-and-you’re-out deadlines are flagged like any other case deadline.' },
      { q: 'Does it replace my immigration practice software?', a: 'It replaces the spreadsheet + Word doc + Outlook combination most small practices actually run on. If you’re happy with Migration Manager or LEAP, keep them — this is for the agencies that aren’t.' },
    ]"
    pricing_callout="One tool for the agent + the client + the file."
  />
</template>
